import * as moment from "moment-timezone";
import * as queryString from "query-string";
import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled, { css } from "styled-components";
import { ColumnFiltersState } from "@tanstack/react-table";

import { useWindowSize } from "src/library/hooks/useWindowSize";
import { useLocalStorage } from "src/library/hooks";
import { Table } from "src/core/dashboard/components/widgets/index";
import { Select } from "src/library/components/Forms";
import { LikeToDo } from "src/library/components/LikeToDo";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import { OrdersCalendar } from "src/core/orders/components/OrdersCalendar";
import { DayWithDetail } from "src/core/orders/components/OrdersCalendar/OrdersCalendar";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { getOrderTableColumns } from "src/core/shared/components/tables/ordersTableColumns";
import { getOrderStatusIdFromCode } from "src/core/orders/utils/orderStatus";
import {
  ListOrdersByDropParams,
  ordersApi,
  OrdersResponse,
  IOrder,
  OrderOrigin
} from "src/core/orders/api/orders.api";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import { setOrdersWithErrors } from "src/core/orders/store/orders.actions";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import { IOrderModeSetting } from "src/core/drops/api/drops.api";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { CreateOrderContext } from "src/core/shared/components/HeaderContext";
import { useOrder } from "src/core/orders/hooks";
import { normalizeDateRangeForTimezone } from "src/library/utils/moment/normalizeDateRangeForTimezone";

interface PageDisplaySettings {
  viewType: OrdersViewType;
  allowViewToggle: boolean;
  orderMode: IOrderModeSetting["allow"];
}

export type OrdersViewType = "calendar" | "table";

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "updatedAt", "DESC" ] ]
};

const defaultViewType: PageDisplaySettings = {
  orderMode: "ANY",
  viewType: "calendar",
  allowViewToggle: true
};

const OrdersPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { width } = useWindowSize();

  const {
    history, terminology, location
  } = props;

  const [ pageDisplaySettingsString, setPageDisplaySettingsString ] = useLocalStorage("ordersPageDisplay", JSON.stringify({
    ...defaultViewType,
    viewType: "calendar"
  }));

  const pageDisplaySettings: PageDisplaySettings = React.useMemo(() => {
    return JSON.parse(pageDisplaySettingsString);
  }, [ pageDisplaySettingsString ]);

  const setPageDisplaySettings = React.useCallback((pageSettings: PageDisplaySettings) => {
    setPageDisplaySettingsString(JSON.stringify(pageSettings));
  }, [ setPageDisplaySettingsString ]);

  const dispatch = useDispatch();
  const pricesVisible = usePriceVisibility();

  const {
    orderStatuses, role, erp, installedModules
  } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const [ { isCreatingOrder }, { createOrder } ] = useOrder();
  const [ { drop, dropIdMemo } ] = useDrop();
  const [ ordersResponse, getOrders ] = ordersApi<OrdersResponse>("LIST_DROP_ORDERS");
  const [ defaultFilters, setDefaultFilters ] = React.useState<ColumnFiltersState | null>(null);
  const [ filterNaturalLanguage, setFilterNaturalLanguage ] = React.useState<string>("");
  const ordersData = React.useMemo(() => ordersResponse.data, [ ordersResponse.data ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IOrder>, pagination: PaginatedParams<IOrder>}) => {
      const { orderStatus, ...filters } = params.filters;

      if (dropIdMemo && defaultFilters) {
        getOrders<ListOrdersByDropParams>({
          pagination: params.pagination,
          filters: {
            ...filters,
            orderStatusId: orderStatus ? parseInt(orderStatus, 10) : undefined
          },
          dropId: dropIdMemo
        });
      }
    },
    [ dropIdMemo, defaultFilters ]
  );

  React.useEffect(() => {
    let viewType = pageDisplaySettings.viewType;
    let allowViewToggle = pageDisplaySettings.allowViewToggle;

    if (filterNaturalLanguage.length) {
      viewType = "table";
      allowViewToggle = false;
    } else {
      allowViewToggle = true;
    }

    setPageDisplaySettings({
      ...pageDisplaySettings,
      allowViewToggle,
      viewType
    });
  }, [
    pageDisplaySettings,
    location.state,
    filterNaturalLanguage,
    props.roles,
    setPageDisplaySettings
  ]);

  const getDataForCalandar = React.useCallback(
    (month: Date) => {
      if (pageDisplaySettings) {
        const cancelledOrderStatus = getOrderStatusIdFromCode(orderStatuses, "cancelled");

        let startOfMonth = moment
          .utc(month)
          .startOf("month");

        const startOfMonthDayOfWeek = startOfMonth.weekday();

        startOfMonth = startOfMonth.subtract(startOfMonthDayOfWeek - 1, "days");

        const endDate = moment.utc(startOfMonth).add(35, "days");

        if (dropIdMemo) {
          getOrders<ListOrdersByDropParams>({
            pagination: {
              ...defaultPagination,
              limit: 100
            },
            filters: {
              orderStatusId: { $not: cancelledOrderStatus },
              orderDeliveryDate: { $between: [ startOfMonth.startOf("day").toDate(), endDate.endOf("day").toDate() ] }
            },
            dropId: dropIdMemo
          });
        }
      }
    },
    [
      dropIdMemo,
      orderStatuses,
      pageDisplaySettings
    ]
  );

  // Get the context filters from the URL query parameters.
  React.useEffect(() => {
    if (!defaultFilters) {
      const query = queryString.parse(location.search);
      const filterObject: ColumnFiltersState = [];
      const naturallanguagePrefix = `Showing ${props.terminology.order.plural.text} `;
      const naturalLanguagePieces: string[] = [];
      let naturalLanguage = "";

      if (query.filters) {
        const queryFilters = query.filters as string;
        const filters = queryFilters.split(",");
        const filtersArray = filters.map(filterElement => filterElement.split(":"));

        // TODO: Move this to a a function.
        const filtersFormatted = filtersArray.reduce((p: { [filterName: string]: string }, c) => {
          p[ c[ 0 ] ] = c[ 1 ];

          return p;
        }, {});

        if (filtersFormatted.deliveryDate) {
          const { from, to } = normalizeDateRangeForTimezone({
            from: filtersFormatted.deliveryDate,
            snapToRangeBoundaries: true
          });

          filterObject.push({
            id: "orderDeliveryDate",
            value: {
              to,
              from
            }
          });
          naturalLanguagePieces.push(`to be delivered on ${moment.utc(filtersFormatted.deliveryDate, "YYYY-MM-DD").format("DD MMM YYYY")}`);
        }
      }

      // Set context natural language explanation.
      if (naturalLanguagePieces.length > 0) {
        naturalLanguage = `${naturallanguagePrefix} ` + naturalLanguagePieces.join(", ");
      }

      setFilterNaturalLanguage(naturalLanguage);
      setDefaultFilters(filterObject);
    }
  }, [
    defaultFilters,
    location.search,
    orderStatuses,
    props.terminology.order.plural.text
  ]);

  const viewOrder = (id: number) => {
    history.push(`/orders/${id}`);
  };

  const handleCalendarDayPick = (date: Date, detail: DayWithDetail) => {
    let isDayMultiOrder: boolean = detail.details.length > 1;

    isDayMultiOrder = isDayMultiOrder || detail.details.some(orderData => orderData.qty > 1);

    if (!isDayMultiOrder) {
      history.push(`/orders/${detail.details[ 0 ].id}`);
    } else {
      setDefaultFilters(null);

      if (pageDisplaySettings) {
        setPageDisplaySettings({
          ...pageDisplaySettings,
          viewType: "table"
        });
      }

      history.push(
        `/orders?filters=deliveryDate:${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
      );
    }
  };

  const toggleView = (viewType: PageDisplaySettings["viewType"]) => {
    setPageDisplaySettings({
      ...pageDisplaySettings,
      viewType
    });

    if (pageDisplaySettings.viewType !== "calendar") {
      // Make sure query parameters are removed.
      history.push("/orders");
    }
  };

  React.useEffect(() => {
    if (ordersResponse.data) {
      dispatch(setOrdersWithErrors(ordersResponse.data.allOrdersWithErrors));
    }
  }, [ ordersResponse ]);

  const tableColumns = React.useMemo(() => {
    if (!dropIdMemo || !erp || !installedModules || !orderStatuses.length || !role || !terminology) {
      return [];
    }

    return getOrderTableColumns({
      installedModules,
      terminology,
      orderStatuses,
      pricesVisible,
      actionButton: info => {
        if (dropIdMemo && erp && role) {
          return (
            <OrderActionsList
              erp={erp}
              terminology={terminology}
              role={role}
              order={info.row.original}
            />
          );
        }

        return null;
      }
    });
  }, [
    dropIdMemo,
    erp,
    installedModules,
    orderStatuses,
    pricesVisible,
    role,
    terminology
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        {pageDisplaySettings.viewType === "table" && (
          <>
            <LikeToDo
              copy="What would you like to do?"
              buttons={[
                <CreateOrderContext
                  direction="left"
                  key={0}
                  drop={drop}
                  loading={isCreatingOrder}
                  createDraftOrder={deliveryDate => createOrder(deliveryDate, OrderOrigin.Manual)}
                />
              ]}
            />
          </>
        )}

        {pageDisplaySettings && (
          <PageWrapper>
            {!width ||
              (width > 960 && pageDisplaySettings.allowViewToggle && (
                <>
                  <div className="set-view">
                    <Select
                      as="select"
                      name="view-type"
                      label="View as"
                      value={pageDisplaySettings.viewType}
                      handleChange={e => toggleView(e.target.value)}
                      options={[
                        {
                          text: "Calendar",
                          value: "calendar"
                        },
                        {
                          text: "Table",
                          value: "table"
                        }
                      ]}
                    />
                  </div>
                </>
              ))}

            {pageDisplaySettings.viewType === "calendar" && (
              <OrdersCalendar
                orderStatuses={orderStatuses}
                date={new Date()}
                orders={ordersData ? ordersData.orders.result : []}
                onDateClick={handleCalendarDayPick}
                handleMonthChange={getDataForCalandar}
                loading={ordersResponse.pending}
              />
            )}

            {pageDisplaySettings.viewType === "table" && defaultFilters && (
              <Table
                tableOf={terminology.order.plural.text}
                className="large"
                data={ordersData ? ordersData.orders.result : []}
                onFetchData={onFetchData}
                defaultPagination={defaultPagination}
                loading={ordersResponse.pending}
                defaultFilters={defaultFilters}
                pageCount={ordersData ? ordersData.orders.pages : 0}
                rowCount={ordersData ? ordersData.orders.count : 0}
                error={ordersResponse.error}
                columns={tableColumns}
                defaultVisibility={{
                  id: false,
                  updatedAt: false
                }}
                mainAction={info => viewOrder(info.original.id)}
              />
            )}
          </PageWrapper>
        )}
      </MainContentWrapper>
    </Layout>
  );
};

const PageWrapper = styled.div`
  ${({ theme }) => css`
    
    .set-view {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-bottom: ${theme.spacing.margins.md};

      label {
        padding-bottom: 0;
        margin-right: ${theme.spacing.margins.sm};
      }
      
      > div {
        max-width: 200px;
      }
    }
  `}
`;

export default OrdersPage;
